import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, dateFmt, money, LEAD_STAGES, STAGE_LABEL, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import StatusBadge from "@/components/StatusBadge";
import SpaceEntries from "@/components/SpaceEntries";
import LeadMessages from "@/components/LeadMessages";
import { BookingForm } from "@/components/AccountsPanel";
import {
  ArrowLeft, Phone, EnvelopeSimple, Globe, MapPin, UserPlus, ClockCounterClockwise,
  PaperPlaneRight, Buildings, Paperclip, FileArrowDown, X, ArrowRight,
  BellRinging, CalendarPlus, Trash, ClockClockwise, Check, Tag, Ticket,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const MAX_FILE = 5 * 1024 * 1024;

function readFile(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve({ name: file.name, data: r.result, type: file.type });
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function Attachment({ file }) {
  if (!file) return null;
  const isImg = (file.type || "").startsWith("image/");
  if (isImg) {
    return (
      <a href={file.data} target="_blank" rel="noreferrer" data-testid="activity-file" className="mt-2 inline-block">
        <img src={file.data} alt={file.name} className="max-h-28 border border-[#E5E7EB]" />
        <span className="block text-xs text-[#4B5563] mt-1">{file.name}</span>
      </a>
    );
  }
  return (
    <a href={file.data} download={file.name} data-testid="activity-file"
      className="mt-2 inline-flex items-center gap-1.5 border border-[#E5E7EB] px-3 py-1.5 text-xs text-[#002FA7] hover:border-[#002FA7] transition-colors">
      <FileArrowDown size={14} weight="bold" /> {file.name}
    </a>
  );
}

export default function LeadDetail() {
  const { leadId } = useParams();
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [note, setNote] = useState("");
  const [noteFile, setNoteFile] = useState(null);
  const [noteKind, setNoteKind] = useState("note");
  const [stageTarget, setStageTarget] = useState(null);
  const [stageNote, setStageNote] = useState("");
  const [stageFile, setStageFile] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);

  const { data: lead, isError } = useQuery({ queryKey: ["lead", leadId], queryFn: () => api.lead(leadId) });
  const { data: bookProps = [] } = useQuery({ queryKey: ["my-properties"], queryFn: () => api.myProperties() });
  const { data: allBk = [] } = useQuery({ queryKey: ["all-bookings"], queryFn: () => api.allBookings() });
  const { data: sales = [] } = useQuery({
    queryKey: ["assignable", lead?.hotel_id],
    queryFn: () => api.listAssignable(lead?.hotel_id),
    enabled: !!lead && ["hotel_gm", "gm", "rm", "dos"].includes(user?.role),
  });

  const assign = useMutation({
    mutationFn: (sales_id) => api.assignLead(leadId, sales_id),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Lead assigned"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [delReason, setDelReason] = useState("");
  const removeLead = useMutation({
    mutationFn: () => api.deleteLead(leadId, delReason.trim()),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["leads"] });
      qc.invalidateQueries({ queryKey: ["deleted-leads"] });
      toast.success("Lead deleted");
      navigate(user.role === "sales" ? "/sales" : ["gm", "rm", "dos"].includes(user.role) ? "/gm?tab=leads" : "/hotel?tab=leads");
    },
    onError: (e) => toast.error(errMsg(e, "Could not delete lead")),
  });
  const setStatus = useMutation({
    mutationFn: ({ status, n, f }) => api.leadStatus(leadId, status, n, f),
    onSuccess: () => {
      qc.invalidateQueries(); toast.success("Stage updated");
      setStageTarget(null); setStageNote(""); setStageFile(null);
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const addNote = useMutation({
    mutationFn: ({ text, f, kind }) => api.leadActivity(leadId, text, f, kind),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["lead", leadId] }); setNote(""); setNoteFile(null); setNoteKind("note"); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const ACTIVITY_OPTIONS = [
    ["note", "Note"], ["prospect_email", "Prospect email"], ["follow_up_email", "Follow-up email"],
    ["warm_call", "Warm call"], ["cold_call", "Cold call"], ["proposal_sent", "Proposal sent"],
    ["property_tour", "Property tour"], ["contract_sent", "Contract sent"],
    ["block_created", "Block created"], ["beo_created", "BEO created"],
  ];

  const [rDate, setRDate] = useState("");
  const [rNote, setRNote] = useState("");
  const addReminder = useMutation({
    mutationFn: () => api.addReminder(leadId, rDate, rNote),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Reminder set"); setRDate(""); setRNote(""); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const toggleReminder = useMutation({
    mutationFn: (rid) => api.toggleReminder(leadId, rid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lead", leadId] }),
  });
  const delReminder = useMutation({
    mutationFn: (rid) => api.deleteReminder(leadId, rid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lead", leadId] }),
  });

  const [pb, setPb] = useState({ start_date: "", end_date: "", room_nights: "", rate: "", notes: "" });
  const addPast = useMutation({
    mutationFn: () => api.addPastBusiness(leadId, { ...pb, room_nights: Number(pb.room_nights) || 0, rate: Number(pb.rate) || 0 }),
    onSuccess: () => { qc.invalidateQueries(); toast.success("Past business logged"); setPb({ start_date: "", end_date: "", room_nights: "", rate: "", notes: "" }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const delPast = useMutation({
    mutationFn: (eid) => api.deletePastBusiness(leadId, eid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["lead", leadId] }),
  });

  const [emailOpen, setEmailOpen] = useState(false);
  const [emForm, setEmForm] = useState({ to_email: "", message: "", fileIdx: "", upload: null });
  const emailContract = useMutation({
    mutationFn: () => {
      const files = (lead.activities || []).filter((a) => a.file).map((a) => a.file);
      const picked = emForm.fileIdx !== "" ? files[Number(emForm.fileIdx)] : null;
      const chosen = emForm.upload || picked;
      return api.emailLeadContract(leadId, {
        to_email: emForm.to_email.trim(),
        message: emForm.message.trim(),
        link_base: window.location.origin,
        attachment: chosen ? { name: chosen.name, data: chosen.data } : null,
      });
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["lead", leadId] });
      if (res.sent) toast.success(`Contract emailed to ${res.delivered_to} & saved to timeline`);
      else if (!res.email_configured) toast.error("Email isn't set up yet on the server.");
      else toast.error("Couldn't deliver (test mode) — saved to timeline instead.");
      setEmailOpen(false);
    },
    onError: (e) => toast.error(errMsg(e, "Could not send the contract")),
  });
  const pickContractFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE) return toast.error("File must be under 5MB");
    const uploaded = await readFile(file);
    setEmForm((s) => ({ ...s, upload: uploaded }));
    e.target.value = "";
  };

  const pickNoteFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE) return toast.error("File must be under 5MB");
    setNoteFile(await readFile(file));
    e.target.value = "";
  };
  const pickStageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE) return toast.error("File must be under 5MB");
    setStageFile(await readFile(file));
    e.target.value = "";
  };

  if (isError) return <div className="max-w-4xl mx-auto px-8 py-20">Lead not found or not accessible.</div>;
  if (!lead) return <div className="max-w-4xl mx-auto px-8 py-20 text-[#4B5563]" data-testid="lead-loading">Loading lead…</div>;

  const back = user.role === "sales" ? "/sales" : ["gm", "rm", "dos"].includes(user.role) ? "/gm?tab=leads" : "/hotel?tab=leads";
  const agreementTotals = (lead.space_entries || []).reduce((a, e) => {
    if ((e.entry_type || "agreement") === "agreement") { a.rn += Number(e.room_nights) || 0; a.value += Number(e.revenue) || 0; }
    return a;
  }, { rn: 0, value: 0 });
  const leadBookings = (allBk || []).filter((b) => b.source_lead_id === leadId);
  const actualTotals = leadBookings.filter((b) => b.status !== "cancelled").reduce((a, b) => { a.rn += Number(b.room_nights) || 0; a.value += Number(b.value) || 0; return a; }, { rn: 0, value: 0 });

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-8">
      <button onClick={() => navigate(back)} data-testid="lead-back"
        className="inline-flex items-center gap-2 text-sm text-[#4B5563] hover:text-[#002FA7] transition-colors">
        <ArrowLeft size={16} weight="bold" /> Back
      </button>
      {["hotel_gm", "gm", "rm", "dos"].includes(user.role) && (
        <button onClick={() => setConfirmDelete(true)} data-testid="lead-delete-btn"
          className="float-right inline-flex items-center gap-2 text-sm text-[#DC2626] border border-[#DC2626] px-3 py-1.5 hover:bg-[#DC2626] hover:text-white transition-colors">
          <Trash size={15} weight="bold" /> Delete lead
        </button>
      )}

      <div className="grid lg:grid-cols-12 gap-6 mt-6">
        <div className="lg:col-span-7 space-y-6">
          <div className="border border-[#111827] p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Buildings size={22} weight="duotone" color="#002FA7" />
                <span className="overline text-[#4B5563]">{lead.industry || "Prospect"}</span>
                {lead.category && (
                  <span data-testid="lead-category" className="inline-flex items-center gap-1 bg-[#EDE9FE] text-[#6D28D9] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider">
                    <Tag size={11} weight="fill" /> {lead.category}
                  </span>
                )}
              </div>
              <StatusBadge status={lead.status} testid="lead-status" />
            </div>
            <h1 className="font-display font-black text-3xl text-[#111827] mt-2">{lead.company_name}</h1>
            <div className="flex items-center gap-1 text-sm text-[#4B5563] mt-1">
              <MapPin size={14} weight="fill" /> {lead.distance_miles} mi from {lead.hotel_name}
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-5 text-sm">
              {lead.address && <div className="sm:col-span-2 text-[#4B5563]">{lead.address}</div>}
              {lead.phone && <div className="flex items-center gap-2 text-[#111827]"><Phone size={15} weight="fill" color="#002FA7" /> {lead.phone}</div>}
              {lead.email && <div className="flex items-center gap-2 text-[#111827] truncate"><EnvelopeSimple size={15} weight="fill" color="#002FA7" /> {lead.email}</div>}
              {lead.website && <a href={`https://${lead.website.replace(/^https?:\/\//, "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#002FA7] hover:underline"><Globe size={15} weight="fill" /> {lead.website}</a>}
            </div>
          </div>

          {/* Pipeline stage control */}
          <div className="border border-[#E5E7EB] p-6">
            <span className="overline text-[#4B5563]">Pipeline stage</span>
            <p className="text-xs text-[#9CA3AF] mt-1">Click a stage to move the lead — you can add a note & attach a file.</p>
            <div className="flex flex-wrap gap-2 mt-3">
              {LEAD_STAGES.map((s) => (
                <button key={s} data-testid={`stage-${s}`}
                  onClick={() => { setStageTarget(s); setStageNote(""); setStageFile(null); }}
                  className={`px-3 py-2 text-sm font-semibold border transition-colors active:scale-[0.97] ${lead.status === s ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-[#E5E7EB] text-[#4B5563] hover:border-[#111827]"}`}>
                  {STAGE_LABEL[s]}
                </button>
              ))}
            </div>
            <button data-testid="email-contract-btn" onClick={() => { setEmForm({ to_email: lead.email || "", message: "", fileIdx: "", upload: null }); setEmailOpen(true); }}
              className="mt-4 inline-flex items-center gap-2 border border-[#002FA7] text-[#002FA7] px-4 py-2.5 text-sm font-semibold hover:bg-[#E0E7FF] transition-colors active:scale-[0.98]">
              <EnvelopeSimple size={16} weight="bold" /> Email contract to client
            </button>
          </div>

          {/* Client conversation */}
          <LeadMessages leadId={leadId} clientEmail={lead.email || ""} clientName={lead.company_name || ""} />

          {/* Activity log */}
          <div className="border border-[#E5E7EB] p-6">
            <div className="flex items-center gap-2">
              <ClockCounterClockwise size={18} weight="bold" color="#002FA7" />
              <span className="font-semibold text-[#111827]">Activity, notes & files</span>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); if (note.trim() || noteFile) addNote.mutate({ text: note.trim(), f: noteFile, kind: noteKind }); }}
              className="mt-4">
              <div className="flex items-center gap-2">
                <select data-testid="lead-note-kind" value={noteKind} onChange={(e) => setNoteKind(e.target.value)}
                  className="border border-[#E5E7EB] px-2 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                  {ACTIVITY_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
                <input data-testid="lead-note-input" value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Log a call, email or note…" className="flex-1 border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
                <label data-testid="lead-note-attach" className="p-3 border border-[#E5E7EB] cursor-pointer hover:border-[#002FA7] transition-colors" title="Attach file">
                  <Paperclip size={18} weight="bold" color="#4B5563" />
                  <input data-testid="lead-note-file" type="file" onChange={pickNoteFile} className="hidden" />
                </label>
                <button data-testid="lead-note-btn" type="submit" disabled={addNote.isPending}
                  className="bg-[#111827] text-white p-3 hover:bg-[#002FA7] transition-colors active:scale-[0.95] disabled:opacity-60"><PaperPlaneRight size={18} weight="fill" /></button>
              </div>
              {noteFile && (
                <div className="mt-2 inline-flex items-center gap-2 bg-[#E0E7FF] text-[#002FA7] px-3 py-1.5 text-xs" data-testid="lead-note-file-chip">
                  <Paperclip size={13} weight="bold" /> {noteFile.name}
                  <button type="button" onClick={() => setNoteFile(null)}><X size={13} weight="bold" /></button>
                </div>
              )}
            </form>

            <div className="mt-5 space-y-3" data-testid="lead-activities">
              {(lead.activities || []).length === 0 && <p className="text-sm text-[#9CA3AF]">No activity yet.</p>}
              {[...(lead.activities || [])].reverse().map((a, i) => (
                <div key={i} className="flex gap-3 text-sm border-l-2 border-[#E0E7FF] pl-3">
                  <div className="flex-1">
                    <div className="text-[#111827]">
                      {a.kind && a.kind !== "note" && a.kind !== "stage" && (
                        <span className="inline-block bg-[#EEF2FF] text-[#002FA7] px-1.5 py-0.5 text-[10px] font-semibold uppercase mr-1 align-middle">{(ACTIVITY_OPTIONS.find((o) => o[0] === a.kind) || [null, a.kind])[1]}</span>
                      )}
                      {a.text}
                    </div>
                    <Attachment file={a.file} />
                    <div className="text-xs text-[#9CA3AF] mt-0.5">{a.by} · {dateFmt(a.at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Reminders */}
          <div className="border border-[#E5E7EB] p-6" data-testid="reminders-card">
            <div className="flex items-center gap-2">
              <BellRinging size={18} weight="bold" color="#002FA7" />
              <span className="font-semibold text-[#111827]">Reminders & repeat business</span>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); if (!rDate) return toast.error("Pick a date"); addReminder.mutate(); }}
              className="flex flex-col sm:flex-row gap-2 mt-4">
              <input data-testid="reminder-date" type="date" value={rDate} onChange={(e) => setRDate(e.target.value)}
                className="border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              <input data-testid="reminder-note" value={rNote} onChange={(e) => setRNote(e.target.value)}
                placeholder="e.g. Group returns Aug 2027" className="flex-1 border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              <button data-testid="reminder-add-btn" type="submit" disabled={addReminder.isPending}
                className="bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] flex items-center gap-1.5">
                <CalendarPlus size={16} weight="bold" /> Set
              </button>
            </form>
            <div className="mt-4 space-y-2" data-testid="reminders-list">
              {(lead.reminders || []).length === 0 && <p className="text-sm text-[#9CA3AF]">No reminders set.</p>}
              {[...(lead.reminders || [])].sort((a, b) => a.date.localeCompare(b.date)).map((r) => (
                <div key={r.id} data-testid={`reminder-${r.id}`} className={`flex items-center justify-between border px-3 py-2 text-sm ${r.done ? "border-[#E5E7EB] opacity-60" : "border-[#E0E7FF] bg-[#F8FAFF]"}`}>
                  <div className="flex items-center gap-2">
                    <button data-testid={`reminder-toggle-${r.id}`} onClick={() => toggleReminder.mutate(r.id)}
                      className={`w-5 h-5 flex items-center justify-center border ${r.done ? "bg-[#047857] border-[#047857] text-white" : "border-[#9CA3AF]"}`}>
                      {r.done && <Check size={12} weight="bold" />}
                    </button>
                    <div>
                      <span className={`font-mono font-semibold ${r.done ? "line-through text-[#9CA3AF]" : "text-[#002FA7]"}`}>{dateFmt(r.date)}</span>
                      <span className="text-[#111827] ml-2">{r.note}</span>
                    </div>
                  </div>
                  <button data-testid={`reminder-del-${r.id}`} onClick={() => delReminder.mutate(r.id)} className="text-[#DC2626] hover:scale-110 transition-transform"><Trash size={15} weight="bold" /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Past business */}
          <div className="border border-[#E5E7EB] p-6" data-testid="past-business-card">
            <div className="flex items-center gap-2">
              <ClockClockwise size={18} weight="bold" color="#002FA7" />
              <span className="font-semibold text-[#111827]">Past business</span>
            </div>
            <form onSubmit={(e) => { e.preventDefault(); if (!pb.start_date) return toast.error("Add a start date"); addPast.mutate(); }}
              className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
              <input data-testid="pb-start" type="date" value={pb.start_date} onChange={(e) => setPb((s) => ({ ...s, start_date: e.target.value }))}
                className="border border-[#E5E7EB] px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              <input data-testid="pb-end" type="date" value={pb.end_date} onChange={(e) => setPb((s) => ({ ...s, end_date: e.target.value }))}
                className="border border-[#E5E7EB] px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              <input data-testid="pb-nights" type="number" placeholder="Room-nights" value={pb.room_nights} onChange={(e) => setPb((s) => ({ ...s, room_nights: e.target.value }))}
                className="border border-[#E5E7EB] px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              <input data-testid="pb-rate" type="number" min="0" placeholder="Rate / nt ($)" value={pb.rate} onChange={(e) => setPb((s) => ({ ...s, rate: e.target.value }))}
                className="border border-[#E5E7EB] px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
              <button data-testid="pb-add-btn" type="submit" disabled={addPast.isPending}
                className="bg-[#111827] text-white px-3 py-2 text-sm font-semibold hover:bg-[#002FA7] transition-colors active:scale-[0.98]">Log</button>
              <input data-testid="pb-notes" value={pb.notes} onChange={(e) => setPb((s) => ({ ...s, notes: e.target.value }))}
                placeholder="Notes (crew size, event…)" className="col-span-2 sm:col-span-5 border border-[#E5E7EB] px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            </form>
            <div className="mt-4 space-y-2" data-testid="past-business-list">
              {(lead.past_business || []).length === 0 && <p className="text-sm text-[#9CA3AF]">No past business logged.</p>}
              {[...(lead.past_business || [])].sort((a, b) => (b.start_date || "").localeCompare(a.start_date || "")).map((p) => (
                <div key={p.id} data-testid={`pb-${p.id}`} className="flex items-center justify-between border border-[#E5E7EB] px-3 py-2 text-sm">
                  <div>
                    <span className="font-mono font-semibold text-[#111827]">{dateFmt(p.start_date)}{p.end_date ? ` – ${dateFmt(p.end_date)}` : ""}</span>
                    {p.room_nights ? <span className="ml-2 bg-[#F3F4F6] px-1.5 py-0.5 text-xs">{p.room_nights} room-nights</span> : null}
                    {p.rate ? <span className="ml-2 bg-[#E0E7FF] text-[#002FA7] px-1.5 py-0.5 text-xs font-semibold">{money(p.rate)}/nt</span> : null}
                    {p.notes && <div className="text-[#4B5563] text-xs mt-0.5">{p.notes}</div>}
                  </div>
                  <button data-testid={`pb-del-${p.id}`} onClick={() => delPast.mutate(p.id)} className="text-[#DC2626] hover:scale-110 transition-transform"><Trash size={15} weight="bold" /></button>
                </div>
              ))}
            </div>
          </div>

          <SpaceEntries leadId={leadId} hotelId={lead.hotel_id} entries={lead.space_entries || []} />

          {/* Agreement -> Actual: create the confirmed booking on the account */}
          {leadBookings.length > 0 ? (
            <div className="border border-[#047857] bg-[#F0FDF4] p-4" data-testid="lead-booked-panel">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="inline-flex items-center gap-2 text-[#047857] font-display font-bold" data-testid="lead-booked-badge">
                  <Check size={18} weight="bold" /> Booked ✓ · {leadBookings.length} booking{leadBookings.length > 1 ? "s" : ""}
                </span>
                <button data-testid="book-actual-btn" onClick={() => setBookOpen(true)} className="text-sm font-semibold text-[#047857] hover:underline">+ Add another</button>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-3" data-testid="agreement-actual-compare">
                <div className="bg-white border border-[#E5E7EB] px-3 py-2">
                  <div className="overline text-[#4B5563]">Agreement (contracted)</div>
                  <div className="text-sm font-semibold text-[#111827] mt-0.5">{agreementTotals.rn} rm-nts · {money(agreementTotals.value)}</div>
                </div>
                <div className="bg-white border border-[#047857] px-3 py-2">
                  <div className="overline text-[#4B5563]">Actual (booked)</div>
                  <div className="text-sm font-semibold text-[#047857] mt-0.5">{actualTotals.rn} rm-nts · {money(actualTotals.value)}</div>
                </div>
              </div>
              <div className="mt-2 space-y-1">
                {leadBookings.map((b) => (
                  <div key={b.id} className="text-xs text-[#4B5563] flex items-center justify-between" data-testid={`lead-booking-${b.id}`}>
                    <span>{b.name} · {b.status} · {b.check_in}{b.check_out ? `–${b.check_out}` : ""}</span>
                    <span className="font-semibold text-[#111827]">{money(b.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="border border-[#047857] bg-[#F0FDF4] p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="actual-booking-cta">
              <div className="text-sm text-[#111827]">
                <b>Agreement contracted?</b> When it's actually booked, create the confirmed booking on this company's account.
              </div>
              {lead.account_id ? (
                <button data-testid="book-actual-btn" onClick={() => setBookOpen(true)}
                  className="inline-flex items-center gap-2 bg-[#047857] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#036249] transition-colors active:scale-[0.98] whitespace-nowrap">
                  <Ticket size={16} weight="bold" /> Book actual →
                </button>
              ) : (
                <span className="text-xs text-[#B45309]">Link this lead to an account first to create a booking.</span>
              )}
            </div>
          )}
        </div>

        <Dialog open={bookOpen} onOpenChange={setBookOpen}>
          <DialogContent className="rounded-none border-[#111827] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-booking-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><Ticket size={20} weight="bold" color="#047857" /> Actual booking</DialogTitle></DialogHeader>
            <p className="text-sm text-[#4B5563] -mt-2">Same booking screen as in Accounts — prefilled from this lead's agreement. Saving files it on <b className="text-[#111827]">{lead.company_name}</b>'s account.</p>
            <BookingForm
              accountId={lead.account_id}
              properties={bookProps}
              initial={{
                name: `${lead.company_name} — actual`,
                hotel_id: lead.hotel_id || "",
                contact_name: lead.contact_name || "",
                check_in: lead.check_in || "",
                check_out: lead.check_out || "",
                rooms: String(lead.rooms_requested || ""),
                room_nights: String(agreementTotals.rn || ""),
                rate: "",
                value: String(agreementTotals.value || ""),
                status: "definite",
                po_number: "",
                notes: `Actual booking from lead: ${lead.company_name}`,
                source_lead_id: leadId,
              }}
              onDone={() => { setBookOpen(false); qc.invalidateQueries({ queryKey: ["lead", leadId] }); }}
            />
          </DialogContent>
        </Dialog>

        {/* Assignment */}
        <div className="lg:col-span-5">
          <div className="border border-[#111827] p-6 lg:sticky lg:top-24">
            <div className="flex items-center gap-2">
              <UserPlus size={18} weight="bold" color="#002FA7" />
              <span className="font-semibold text-[#111827]">Assignment</span>
            </div>
            {lead.assigned_to_name ? (
              <div className="mt-4 bg-[#E0E7FF] text-[#002FA7] px-4 py-3 font-semibold" data-testid="lead-assignee">
                Working: {lead.assigned_to_name}
              </div>
            ) : (
              <div className="mt-4 bg-[#F3F4F6] text-[#4B5563] px-4 py-3 text-sm" data-testid="lead-unassigned">Unassigned</div>
            )}

            {["hotel_gm", "gm", "rm", "dos"].includes(user.role) && (
              <div className="mt-5">
                <span className="overline text-[#4B5563] block mb-2">
                  {lead.assigned_to_name ? "Reassign to" : "Assign to team member"}
                </span>
                {sales.length === 0 ? (
                  <p className="text-sm text-[#4B5563]">
                    No team members yet. <Link to="/hotel?tab=team" className="text-[#002FA7] font-semibold hover:underline">Add a team member →</Link>
                  </p>
                ) : (
                  <div className="space-y-2">
                    {sales.map((s) => (
                      <button key={s.id} data-testid={`assign-to-${s.id}`} onClick={() => assign.mutate(s.id)}
                        className="w-full flex items-center justify-between border border-[#E5E7EB] px-3 py-2.5 text-sm hover:border-[#002FA7] transition-colors active:scale-[0.99]">
                        <span><b className="text-[#111827]">{s.name}</b> <span className="text-[#4B5563]">· {s.title}</span></span>
                        {lead.assigned_to === s.id ? <span className="text-[#047857] text-xs font-semibold">Current</span> : <span className="text-[#002FA7] text-xs font-semibold">{lead.assigned_to_name ? "Reassign" : "Assign"}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stage change dialog */}
      <Dialog open={!!stageTarget} onOpenChange={(o) => { if (!o) setStageTarget(null); }}>
        <DialogContent className="rounded-none border-[#111827] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              Move to <span className="text-[#002FA7]">{stageTarget && STAGE_LABEL[stageTarget]}</span>
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#4B5563] -mt-2">Add an optional note and attach a file for this stage change.</p>
          <label className="text-sm block">
            <span className="overline text-[#4B5563] block mb-1">Note (optional)</span>
            <textarea data-testid="stage-note-input" rows={3} value={stageNote} onChange={(e) => setStageNote(e.target.value)}
              placeholder="e.g. Sent proposal, awaiting sign-off from procurement…"
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          </label>
          <label data-testid="stage-attach" className="flex items-center justify-center gap-2 border border-dashed border-[#9CA3AF] py-3 text-sm text-[#4B5563] cursor-pointer hover:border-[#002FA7] transition-colors">
            <Paperclip size={16} weight="bold" /> {stageFile ? "Change attachment" : "Attach file (optional)"}
            <input data-testid="stage-file" type="file" onChange={pickStageFile} className="hidden" />
          </label>
          {stageFile && (
            <div className="inline-flex items-center gap-2 bg-[#E0E7FF] text-[#002FA7] px-3 py-1.5 text-xs" data-testid="stage-file-chip">
              <Paperclip size={13} weight="bold" /> {stageFile.name}
              <button type="button" onClick={() => setStageFile(null)}><X size={13} weight="bold" /></button>
            </div>
          )}
          <DialogFooter>
            <button data-testid="stage-confirm-btn" disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ status: stageTarget, n: stageNote.trim(), f: stageFile })}
              className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60 flex items-center gap-2">
              {setStatus.isPending ? "Saving…" : <>Confirm move <ArrowRight size={16} weight="bold" /></>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email contract dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="rounded-none border-[#111827] max-w-lg" data-testid="email-contract-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><EnvelopeSimple size={22} weight="bold" color="#002FA7" /> Email contract</DialogTitle></DialogHeader>
          <p className="text-sm text-[#4B5563] -mt-2">Emails <b className="text-[#111827]">{lead.company_name}</b> a branded PDF summary. Replies come straight to your inbox. Attach your own contract file below (optional).</p>
          <div className="space-y-3">
            <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Client email</span>
              <input data-testid="email-contract-to" type="email" value={emForm.to_email} onChange={(e) => setEmForm((s) => ({ ...s, to_email: e.target.value }))}
                placeholder="planner@company.com" className="w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>

            <div className="text-sm">
              <span className="overline text-[#4B5563] block mb-1">Attach contract file (optional)</span>
              {emForm.upload ? (
                <div className="flex items-center justify-between border border-[#E5E7EB] px-3 py-2" data-testid="email-contract-upload-name">
                  <span className="flex items-center gap-2 truncate text-[#111827]"><Paperclip size={15} weight="bold" /> {emForm.upload.name}</span>
                  <button data-testid="email-contract-upload-clear" onClick={() => setEmForm((s) => ({ ...s, upload: null }))} className="text-[#DC2626] shrink-0"><X size={15} weight="bold" /></button>
                </div>
              ) : (
                <label data-testid="email-contract-upload" className="cursor-pointer flex items-center gap-2 border border-dashed border-[#9CA3AF] px-3 py-2.5 text-[#4B5563] hover:border-[#002FA7] hover:text-[#002FA7] transition-colors">
                  <Paperclip size={16} weight="bold" /> Choose a file to attach (PDF, DOCX… up to 5MB)
                  <input type="file" className="hidden" onChange={pickContractFile} />
                </label>
              )}
            </div>

            {(lead.activities || []).some((a) => a.file) && !emForm.upload && (
              <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">…or pick a file already on this lead</span>
                <select data-testid="email-contract-file" value={emForm.fileIdx} onChange={(e) => setEmForm((s) => ({ ...s, fileIdx: e.target.value }))}
                  className="w-full border border-[#E5E7EB] px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
                  <option value="">— PDF summary only —</option>
                  {(lead.activities || []).filter((a) => a.file).map((a, i) => <option key={i} value={i}>{a.file.name}</option>)}
                </select></label>
            )}
            <label className="text-sm block"><span className="overline text-[#4B5563] block mb-1">Message (optional)</span>
              <textarea data-testid="email-contract-message" rows={3} value={emForm.message} onChange={(e) => setEmForm((s) => ({ ...s, message: e.target.value }))}
                placeholder="Thanks for your business — the agreement is attached." className="w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          </div>
          <DialogFooter>
            <button data-testid="email-contract-send" disabled={emailContract.isPending || !emForm.to_email.trim()} onClick={() => emailContract.mutate()}
              className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] disabled:opacity-60 inline-flex items-center gap-2">
              <EnvelopeSimple size={16} weight="bold" /> {emailContract.isPending ? "Sending…" : "Send contract"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent className="rounded-none border-[#111827] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Delete this lead?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#4B5563] -mt-2">
            <b className="text-[#111827]">{lead.company_name}</b> will be moved to the <b>Deleted Leads report</b> (viewable under Bookings) and removed from the pipeline. You can restore it later.
          </p>
          <label className="text-sm block">
            <span className="overline text-[#4B5563] block mb-1">Reason for deleting (optional)</span>
            <textarea data-testid="lead-delete-reason" rows={2} value={delReason} onChange={(e) => setDelReason(e.target.value)}
              placeholder="e.g. Duplicate enquiry / lost to competitor / test entry"
              className="w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#DC2626]" />
          </label>
          <DialogFooter>
            <button data-testid="lead-delete-cancel" onClick={() => setConfirmDelete(false)}
              className="px-6 py-3 font-semibold border border-[#E5E7EB] hover:border-[#111827] transition-colors">
              Cancel
            </button>
            <button data-testid="lead-delete-confirm" disabled={removeLead.isPending}
              onClick={() => removeLead.mutate()}
              className="bg-[#DC2626] text-white px-6 py-3 font-semibold hover:bg-[#B91C1C] transition-colors active:scale-[0.98] disabled:opacity-60 flex items-center gap-2">
              {removeLead.isPending ? "Deleting…" : <><Trash size={16} weight="bold" /> Delete lead</>}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
